import { api } from "@/lib/api";
import { LifecycleStageCheckboxGroup } from "@/components/LifecycleStageCheckboxGroup";
import { SenderIdentitySelect } from "@/components/SenderIdentitySelect";
import {
  Button,
  Checkbox,
  CheckboxField,
  Input,
  Label,
  Switch,
  SwitchField,
  Textarea,
} from "@a7y/tailwind-catalyst";
import type { AnnouncementCreateRequest } from "@a7y/types";
import type { OrgBillingStatus, SenderIdentity } from "@a7y/types";
import * as Headless from "@headlessui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { BILLING_STATUS_OPTIONS } from "./constants";

interface AnnouncementCreateTabProps {
  onCreated: () => void;
}

export function AnnouncementCreateTab({
  onCreated,
}: AnnouncementCreateTabProps) {
  const queryClient = useQueryClient();

  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("");
  const [summary, setSummary] = useState("");
  const [blogPostUrl, setBlogPostUrl] = useState("");
  const [senderIdentity, setSenderIdentity] = useState<SenderIdentity>("team");
  const [targetLifecycleStages, setTargetLifecycleStages] = useState<
    OrgBillingStatus[]
  >(["trialing", "active"]);
  const [includeChurned, setIncludeChurned] = useState(false);
  const [active, setActive] = useState(true);
  const [triggerBroadcast, setTriggerBroadcast] = useState(false);

  const resetForm = () => {
    setTitle("");
    setSubject("");
    setSummary("");
    setBlogPostUrl("");
    setSenderIdentity("team");
    setTargetLifecycleStages(["trialing", "active"]);
    setIncludeChurned(false);
    setActive(true);
    setTriggerBroadcast(false);
  };

  const createMutation = useMutation({
    mutationFn: (data: AnnouncementCreateRequest) =>
      api.createAnnouncement(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcements"] });
      resetForm();
      onCreated();
    },
  });

  const canSubmit =
    title.trim().length > 0 &&
    subject.trim().length > 0 &&
    summary.trim().length > 0 &&
    blogPostUrl.trim().length > 0 &&
    targetLifecycleStages.length > 0;

  const wordCount = summary
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 0).length;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    createMutation.mutate({
      title: title.trim(),
      subject: subject.trim(),
      summary: summary.trim(),
      blogPostUrl: blogPostUrl.trim(),
      senderIdentity,
      targetLifecycleStages,
      includeChurned,
      active,
      triggerBroadcast,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
      {/* Title */}
      <Headless.Field>
        <Label>Title</Label>
        <Input
          className="mt-2"
          value={title}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setTitle(e.target.value)
          }
          placeholder="Internal title for this announcement"
          required
        />
      </Headless.Field>

      {/* Subject */}
      <Headless.Field>
        <Label>Email Subject</Label>
        <Input
          className="mt-2"
          value={subject}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSubject(e.target.value)
          }
          placeholder="Subject line recipients will see"
          required
        />
      </Headless.Field>

      {/* Summary */}
      <Headless.Field>
        <Label>Summary</Label>
        <Textarea
          className="mt-2"
          rows={6}
          value={summary}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setSummary(e.target.value)
          }
          placeholder="Brief summary of the announcement (150-200 words recommended)"
          required
        />
        <p className="mt-1.5 text-sm text-zinc-500 dark:text-zinc-400">
          {wordCount} {wordCount === 1 ? "word" : "words"}
          {wordCount > 0 && wordCount < 150 && " -- below recommended minimum of 150"}
          {wordCount > 200 && " -- above recommended maximum of 200"}
        </p>
      </Headless.Field>

      {/* Blog Post URL */}
      <Headless.Field>
        <Label>Blog Post URL</Label>
        <Input
          className="mt-2"
          type="url"
          value={blogPostUrl}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setBlogPostUrl(e.target.value)
          }
          placeholder="https://www.apptonomy.ai/blog/..."
          required
        />
      </Headless.Field>

      {/* Sender Identity */}
      <SenderIdentitySelect
        value={senderIdentity}
        onChange={(v) => setSenderIdentity(v as SenderIdentity)}
      />

      {/* Target Lifecycle Stages */}
      <LifecycleStageCheckboxGroup
        legend="Target Lifecycle Stages"
        options={BILLING_STATUS_OPTIONS}
        selected={targetLifecycleStages}
        onChange={setTargetLifecycleStages}
      />

      {/* Include Churned */}
      <CheckboxField>
        <Checkbox
          checked={includeChurned}
          onChange={(checked: boolean) => setIncludeChurned(checked)}
          color="blue"
        />
        <Label>Include Churned Users</Label>
      </CheckboxField>

      {/* Active Toggle */}
      <SwitchField>
        <Label>Active</Label>
        <Headless.Description className="text-sm text-zinc-500 dark:text-zinc-400">
          Only active announcements can be delivered via automated email
          sequences or broadcast.
        </Headless.Description>
        <Switch
          checked={active}
          onChange={(checked: boolean) => setActive(checked)}
          color="blue"
        />
      </SwitchField>

      {/* Trigger Immediate Broadcast */}
      <div className="rounded-lg border border-zinc-200 p-4 dark:border-zinc-700">
        <CheckboxField>
          <Checkbox
            checked={triggerBroadcast}
            onChange={(checked: boolean) => setTriggerBroadcast(checked)}
            color="red"
          />
          <Label>Trigger Immediate Broadcast</Label>
        </CheckboxField>
        <p className="mt-2 pl-7 text-sm text-zinc-500 dark:text-zinc-400">
          When enabled, creating this announcement will also immediately
          broadcast it to all matching recipients. This sends real emails and
          cannot be undone. Leave unchecked to add the announcement to the pool
          without sending.
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 border-t border-zinc-200 pt-5 dark:border-zinc-700">
        <Button
          type="submit"
          color="blue"
          disabled={!canSubmit || createMutation.isPending}
        >
          {createMutation.isPending
            ? "Creating..."
            : triggerBroadcast
              ? "Create & Broadcast"
              : "Create Announcement"}
        </Button>
        <Button type="button" plain onClick={resetForm}>
          Reset
        </Button>
      </div>

      {/* Feedback */}
      {createMutation.isSuccess && (
        <div className="rounded-lg bg-green-50 p-3 text-sm text-green-800 dark:bg-green-900/20 dark:text-green-400">
          Announcement created successfully.
        </div>
      )}
      {createMutation.isError && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
          {createMutation.error?.message || "Failed to create announcement"}
        </div>
      )}
    </form>
  );
}
